/* eslint-disable import/no-unassigned-import */
import { useMemo, useRef, useState } from 'react';
import { defineMessages, useIntl } from 'react-intl';
import classNames from 'classnames';
import { A11y, Keyboard, Pagination, Thumbs } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';
import { type Swiper as SwiperClass } from 'swiper/types';

import { BreakpointsPx } from '@jsmdg/imgix';
import {
    Breakpoint,
    ButtonSize,
    Image,
    PureLink,
    ResponsiveFrame,
    Skeleton,
    SkeletonAnimation,
    SkeletonVariant,
    useBreakpoint,
    useIsMounted,
} from '@jsmdg/yoshi';
import 'swiper/css';
import 'swiper/css/thumbs';
import 'swiper/css/pagination';
import {
    GALLERY_IMAGES_PREVIEW_DIMENSIONS,
    PRODUCT_IMG_DIMENSIONS,
} from '../../../shared/dimensions';
import { MediaKind } from '../../../shared/mediaKind';
import { type Product, type ProductSearchImageItem } from '../../../shared/types';
import { buildGalleryMediaItems, type GalleryMediaItem } from './galleryMedia';
import { trackImageSlide, trackThumbnailClicked } from './imageGallery.tracking';
import { ImageGalleryControl } from './ImageGalleryControl';
import { PlayButton } from './PlayButton';
import { Video } from './Video';
import styles from './ImageGallery.module.scss';

const getSlidingDirection = (previousIndex: number, currentIndex: number): string => {
    return previousIndex < currentIndex ? 'Slide next' : 'Slide previous';
};

const messages = defineMessages({
    slideAnnouncement: {
        defaultMessage: 'Bild {current} von {total}',
    },
    thumbnailAriaLabel: {
        defaultMessage: 'Vorschaubild {index} von {total}, {altText}, Schaltfläche',
    },
    playIconAriaLabel: {
        defaultMessage: 'Video abspielen',
    },
});

const ImageGallerySkeleton = (): JSX.Element => {
    const mainDimensions = PRODUCT_IMG_DIMENSIONS[BreakpointsPx.xl];

    return (
        <>
            <ResponsiveFrame width={mainDimensions.width} height={mainDimensions.height}>
                <div className={classNames('w-100 h-100', styles.galleryPlaceholder)}>
                    <Skeleton
                        animation={SkeletonAnimation.Wave}
                        variant={SkeletonVariant.Rect}
                        width="100%"
                        height="100%"
                    />
                </div>
            </ResponsiveFrame>
            <div className={classNames(styles.thumbnailGallerySkeleton, 'd-flex')} aria-hidden>
                {Array.from({ length: 4 }, (_, index) => (
                    <div className={styles.thumbnailSkeleton} key={index}>
                        <Skeleton
                            animation={SkeletonAnimation.Wave}
                            variant={SkeletonVariant.Rect}
                            width="100%"
                            height="100%"
                        />
                    </div>
                ))}
            </div>
        </>
    );
};

type ImageGalleryProps = {
    readonly title: string;
    readonly mediaContent: Product['mediaContent'] | ProductSearchImageItem[];
};

const ImageGallery = ({ mediaContent, title }: ImageGalleryProps): JSX.Element => {
    const intl = useIntl();
    const mainDimensions = PRODUCT_IMG_DIMENSIONS[BreakpointsPx.xl];
    const thumbnailDimensions = GALLERY_IMAGES_PREVIEW_DIMENSIONS;
    const mediaItems = useMemo(() => buildGalleryMediaItems(mediaContent), [mediaContent]);
    const galleryRef = useRef<SwiperClass | null>(null);
    const isDesktopViewport = useBreakpoint(Breakpoint.SM);
    const isThumbnailClicked = useRef(false);
    const [activeSlide, setActiveSlide] = useState<number>(mediaItems.length === 0 ? 0 : 1);
    const [isMouseEntering, setIsMouseEntering] = useState(false);
    const isMounted = useIsMounted();
    const totalSlides = mediaItems.length || 1;
    const isDisabledPrev = activeSlide <= 1;
    const isDisabledNext = activeSlide >= mediaItems.length;

    function handleNavChange(swiperClass: SwiperClass): void {
        const activeMediaItem = mediaItems[swiperClass.activeIndex];
        const clickedMediaURL = activeMediaItem?.trackingUrl || '';

        if (isThumbnailClicked.current) {
            trackThumbnailClicked(clickedMediaURL);
        } else {
            const label = getSlidingDirection(swiperClass.previousIndex, swiperClass.activeIndex);
            trackImageSlide(label, clickedMediaURL);
        }

        galleryRef.current?.slides.forEach((slide, slideIndex) => {
            if (slideIndex !== swiperClass.activeIndex) {
                slide.querySelectorAll('video').forEach(video => {
                    video.pause();
                });
            }
        });

        isThumbnailClicked.current = false;
        setActiveSlide(swiperClass.activeIndex + 1);
        galleryRef.current?.update();
    }

    return (
        <>
            <span aria-live="polite" aria-atomic="true" className="screenReaderOnly">
                {intl.formatMessage(messages.slideAnnouncement, {
                    current: activeSlide,
                    total: mediaItems.length,
                })}
            </span>
            <div
                onMouseEnter={() => {
                    setIsMouseEntering(true);
                }}
                onMouseLeave={() => {
                    setIsMouseEntering(false);
                }}
            >
                {isMounted ? (
                    <Swiper
                        className={classNames('w-100 mx-auto', styles.gallerySwiper)}
                        onSwiper={(gallerySwiper: SwiperClass) => {
                            galleryRef.current = gallerySwiper;
                        }}
                        pagination={{
                            clickable: true,
                            modifierClass: styles.paginationContainer,
                        }}
                        modules={[A11y, Keyboard, Thumbs, Pagination]}
                        watchOverflow
                        onSlideChangeTransitionEnd={swiperInstance =>
                            handleNavChange(swiperInstance)
                        }
                        keyboard={{ enabled: true, onlyInViewport: true }}
                        a11y={{
                            enabled: true,
                            id: 'swiper-wrapper-id',
                            scrollOnFocus: false,
                        }}
                    >
                        <ResponsiveFrame
                            width={mainDimensions.width}
                            height={mainDimensions.height}
                        >
                            {mediaItems.map((mediaItem: GalleryMediaItem, index) => (
                                <SwiperSlide
                                    key={`${mediaItem.key}-gallery-slide`}
                                    className={styles.swiperSlide}
                                    role="group"
                                    aria-hidden={activeSlide - 1 !== index}
                                    tabIndex={activeSlide - 1 === index ? 0 : -1}
                                    onFocus={() => {
                                        setIsMouseEntering(true);
                                    }}
                                >
                                    {mediaItem.kind === MediaKind.IMAGE ? (
                                        <Image
                                            src={mediaItem.src}
                                            alt={`${mediaItem.alt || title} - ${index + 1}`}
                                            srcSet={mediaItem.srcset}
                                            sizes={mediaItem.sizes}
                                            className={classNames('w-100 h-100 d-block')}
                                            lazyLoad={false}
                                            fetchPriority={index === 0 ? 'high' : 'low'}
                                            width={mainDimensions.width}
                                            height={mainDimensions.height}
                                        />
                                    ) : (
                                        <Video
                                            className="w-100 d-block"
                                            poster={mediaItem.poster}
                                            withImagePreview={false}
                                            index={index}
                                            thumbnailAlt={mediaItem.alt ?? title}
                                            ariaLabel={`${title} - Video ${index + 1}`}
                                            width={mainDimensions.width}
                                            height={mainDimensions.height}
                                            sources={mediaItem.sources}
                                            durationInSeconds={mediaItem.durationInSeconds}
                                        />
                                    )}
                                </SwiperSlide>
                            ))}
                        </ResponsiveFrame>
                        <div
                            slot="container-end"
                            className={classNames(
                                'position-absolute d-flex justify-content-center',
                                styles.imageGalleryControlContainer,
                            )}
                        >
                            <ImageGalleryControl
                                show={isMouseEntering && isDesktopViewport}
                                currentSlide={activeSlide}
                                totalSlides={totalSlides}
                                onNext={() => galleryRef.current?.slideNext()}
                                onPrev={() => galleryRef.current?.slidePrev()}
                                isDisabledNext={isDisabledNext}
                                isDisabledPrev={isDisabledPrev}
                            />
                        </div>
                    </Swiper>
                ) : (
                    <ImageGallerySkeleton />
                )}
            </div>
            {isMounted && (
                <Swiper
                    className={styles.thumbnailGallery}
                    slidesPerView={4}
                    spaceBetween={16}
                    roundLengths
                    hidden={!isDesktopViewport}
                    threshold={100}
                    watchSlidesProgress
                    modules={[Thumbs]}
                    onClick={() => {
                        isThumbnailClicked.current = true;
                    }}
                >
                    {mediaItems.map((mediaItem, index) => (
                        <SwiperSlide
                            key={`${mediaItem.key}-thumbnail`}
                            tabIndex={-1}
                            onFocus={() => galleryRef.current?.slideTo(index)}
                        >
                            <PureLink
                                onClick={() => galleryRef.current?.slideTo(index)}
                                className={classNames(
                                    'theme-remove-button-styles',
                                    styles.thumbnailSlide,
                                    {
                                        [styles.activeThumbnailSlide]: index + 1 === activeSlide,
                                    },
                                    'swiper-slide swiper-slide-active swiper-slide-thumb-active swiper-slide-visible swiper-slide-fully-visible',
                                )}
                                aria-label={intl.formatMessage(messages.thumbnailAriaLabel, {
                                    index: index + 1,
                                    total: mediaItems.length,
                                    altText:
                                        mediaItem.kind === MediaKind.IMAGE
                                            ? mediaItem.alt || title
                                            : `Video ${index + 1}`,
                                })}
                                tabIndex={0}
                            >
                                <div className={styles.thumbnailContent}>
                                    <Image
                                        src={mediaItem.thumbnail}
                                        alt={
                                            mediaItem.kind === MediaKind.IMAGE
                                                ? `${mediaItem.alt || title} - ${index + 1}`
                                                : `${title} - Video ${index + 1}`
                                        }
                                        className={classNames(
                                            'w-100 h-100 d-block',
                                            styles.thumbImage,
                                        )}
                                        width={thumbnailDimensions.width}
                                        height={thumbnailDimensions.height}
                                        aria-hidden="true"
                                    />
                                    {mediaItem.kind === MediaKind.VIDEO && (
                                        <PlayButton
                                            noHover
                                            size={ButtonSize.Small}
                                            iconClasses={styles.thumbnailPlayIcon}
                                            a11yText={intl.formatMessage(
                                                messages.playIconAriaLabel,
                                            )}
                                        />
                                    )}
                                </div>
                            </PureLink>
                        </SwiperSlide>
                    ))}
                </Swiper>
            )}
        </>
    );
};

export { ImageGallery };
